/**
 * lib/render-policy/display-videos.ts — video selection/ranking resolver
 * (PIP lean rebuild, WS3). PURE function over pre-fetched fin_videos rows.
 *
 * Returns up to 6 videos ranked for TYPE DIVERSITY then popularity, deduped by
 * channel (max 2/channel) so a PDP shows a spread (review + install + tutorial)
 * rather than six near-identical clips.
 *
 * FALLBACK CONTRACT: empty input → empty output (component self-hides).
 */

export interface VideoRow {
  video_url: string;
  title: string | null;
  channel: string | null;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  view_count: number | null;
  video_type: string | null;
  source: string | null;
}

/** Extract a YouTube video id from watch/youtu.be/embed/shorts URL shapes.
 * Null for non-YouTube URLs (e.g. Amazon CDN HLS) — callers branch on it. */
export function youtubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/
  );
  return m ? m[1] : null;
}

/** Amazon CDN HLS clip (native <video> path, Safari only). */
export function isHlsVideoUrl(url: string | null | undefined): boolean {
  return !!url && url.includes('.m3u8');
}

/**
 * True when the row can play in-page: a YouTube id on either URL field, or an
 * HLS clip. Anything else (Instagram reels, TikTok, retailer pages found by the
 * video search source) has no in-page player and is a dead tile — the
 * component has always dropped these at selection time; this is the same rule
 * lifted out so the SERVER can drop them before serialization.
 */
export function isEmbeddableVideo(v: {
  video_url: string | null;
  source_url?: string | null;
}): boolean {
  return (
    !!v.video_url &&
    (!!youtubeVideoId(v.video_url) || !!youtubeVideoId(v.source_url) || isHlsVideoUrl(v.video_url))
  );
}

/**
 * A `data:` URI is not a thumbnail URL — it is the image itself, inlined. The
 * video search source hands back base64 JPEG thumbnails (4–10 KB each) and
 * 4,947 fin_videos rows across 880 FINs carry one (measured 08/15/2026). A PDP
 * that serializes them ships every one twice: once in the RSC payload and once
 * more for each card it renders. On the sampled WRFC9636RZ PDP that was
 * 33.9 KB of a 26 KB overshoot against the 269 KB HTML budget (FRIDGE-288, the
 * FRIDGE-179 class). Only https:// thumbnails are ever kept; YouTube rows fall
 * back to the i.ytimg.com poster in the component.
 */
export function isInlineDataUri(url: string | null | undefined): boolean {
  return typeof url === 'string' && /^\s*data:/i.test(url);
}

export function safeThumbnailUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (isInlineDataUri(url)) return null;
  return url;
}

/**
 * The server-side gate every fin_videos row passes before it reaches the
 * client payload (app/products/[slug]/page.tsx): drop rows that cannot play
 * in-page, and strip inline data-URI thumbnails. Pure; keeps the input order.
 */
/**
 * Internal source tags → the neutral labels the CLIENT may see. Rows arrive
 * tagged by ingestion pipeline; those pipeline names are internal tooling and
 * the owner's rule (08/28/2026) is that supplier/tool names never appear in
 * served output — the value ends up in the RSC payload and a data-source DOM
 * attribute. 'amazon-featured' keeps the featured-first sort semantic.
 */
const PUBLIC_VIDEO_SOURCE: Record<string, string> = {
  keepa_main: 'amazon-featured',
  keepa_additional: 'amazon',
  keepa: 'amazon',
  searchapi_youtube: 'youtube',
  searchapi: 'youtube',
};

export function prepareVideosForClient<T extends { video_url: string | null; source_url?: string | null; thumbnail_url: string | null; source?: string | null }>(
  rows: T[] | null | undefined
): T[] {
  return (rows ?? [])
    .filter((v) => isEmbeddableVideo(v))
    .map((v) => (isInlineDataUri(v.thumbnail_url) ? { ...v, thumbnail_url: null } : v))
    .map((v) =>
      v.source && PUBLIC_VIDEO_SOURCE[v.source] ? { ...v, source: PUBLIC_VIDEO_SOURCE[v.source] } : v
    );
}

const MAX_VIDEOS = 6;
const MAX_PER_CHANNEL = 2;
const TYPE_RANK: Record<string, number> = {
  review: 0,
  comparison: 1,
  installation: 2,
  tutorial: 3,
  unboxing: 4,
  other: 5,
};

function typeRank(t: string | null): number {
  return TYPE_RANK[t ?? 'other'] ?? 5;
}

/**
 * Diversity-first selection: walk the type order, taking the highest-viewed
 * unused video of each type in turn, then loop until full — so the first picks
 * span distinct types before doubling up on the most common one.
 *
 * Generic so callers with richer row shapes (component FinVideo) get their own
 * type back — the function only reads VideoRow fields.
 */
export function getDisplayVideos<T extends VideoRow>(rows: T[] | null | undefined): T[] {
  const pool = (rows ?? []).filter((v) => v.video_url);
  if (pool.length === 0) return [];

  // Sort within each type by view_count desc (nulls last).
  const byType = new Map<number, T[]>();
  for (const v of pool) {
    const r = typeRank(v.video_type);
    if (!byType.has(r)) byType.set(r, []);
    byType.get(r)!.push(v);
  }
  for (const list of byType.values()) {
    list.sort((a, b) => (b.view_count ?? -1) - (a.view_count ?? -1));
  }

  const orderedTypes = [...byType.keys()].sort((a, b) => a - b);
  const selected: T[] = [];
  const perChannel = new Map<string, number>();
  const usedUrls = new Set<string>();

  // Round-robin across types for diversity.
  let progressed = true;
  while (selected.length < MAX_VIDEOS && progressed) {
    progressed = false;
    for (const t of orderedTypes) {
      if (selected.length >= MAX_VIDEOS) break;
      const list = byType.get(t)!;
      const next = list.find((v) => {
        if (usedUrls.has(v.video_url)) return false;
        // Null-channel rows (legacy manufacturer/google_videos) must not all
        // collapse into one '?' bucket capped at 2 (VIDEOS-RANKING-DEVIATIONS).
        if (!v.channel) return true;
        return (perChannel.get(v.channel) ?? 0) < MAX_PER_CHANNEL;
      });
      if (next) {
        selected.push(next);
        usedUrls.add(next.video_url);
        if (next.channel) perChannel.set(next.channel, (perChannel.get(next.channel) ?? 0) + 1);
        progressed = true;
      }
    }
  }
  return selected;
}
